import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw, ScrollText } from 'lucide-react';
import { api } from '../api/client.js';

/**
 * /audit — table view of recent audit_log events.
 * Filters: event_type (preset options), email (substring), limit.
 */

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All events' },
  { value: 'auth.%', label: 'All auth events' },
  { value: 'auth.login', label: 'Logins' },
  { value: 'auth.logout', label: 'Logouts' },
  { value: 'auth.rejected', label: 'Rejected logins' },
  { value: 'auth.invalid_session', label: 'Invalid sessions' },
  { value: 'auth.oauth_error', label: 'OAuth errors' },
  { value: 'sessions.%', label: 'App-session revokes' },
  { value: 'site.%', label: 'Site events' },
  { value: 'system.%', label: 'System events' },
];

const LIMIT_OPTIONS = [25, 50, 100, 250, 500];

export function Audit() {
  const [filters, setFilters] = useState({ event_type: '', email: '', limit: 50 });
  const [state, setState] = useState({ status: 'loading', events: [] });

  const fetchEvents = useCallback(async () => {
    setState((prev) => ({ ...prev, status: 'loading' }));
    try {
      const qs = new URLSearchParams();
      if (filters.event_type) qs.set('event_type', filters.event_type);
      if (filters.email) qs.set('email', filters.email);
      qs.set('limit', String(filters.limit));

      const data = await api(`/api/audit/recent?${qs.toString()}`);
      setState({ status: 'ok', events: data.events });
    } catch (err) {
      setState({ status: 'error', events: [], message: err.message });
    }
  }, [filters]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const reachedLimit = state.events.length >= filters.limit;

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-sm text-t2">
            Every auth attempt, site action, and system event is recorded here.
          </p>
        </div>
        <button
          type="button"
          onClick={fetchEvents}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-t2 hover:bg-s-3"
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${state.status === 'loading' ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      <Filters filters={filters} onChange={updateFilter} />

      <div className="bg-white rounded-xl border border-bd overflow-hidden">
        {state.status === 'loading' && state.events.length === 0 && (
          <div className="p-12 flex items-center justify-center text-sm text-t3">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading events…
          </div>
        )}

        {state.status === 'error' && (
          <div className="p-6 flex items-start gap-3 text-err">
            <AlertTriangle className="w-5 h-5 mt-0.5" />
            <div>
              <p className="text-sm font-medium">Failed to load audit events</p>
              <p className="mt-0.5 text-xs text-err">{state.message}</p>
            </div>
          </div>
        )}

        {state.status === 'ok' && state.events.length === 0 && (
          <div className="p-12 flex flex-col items-center text-center">
            <ScrollText className="w-10 h-10 text-ic-light mb-3" />
            <p className="text-sm font-medium text-t2">No events match</p>
            <p className="mt-1 text-xs text-t3">Try broadening the filters.</p>
          </div>
        )}

        {state.events.length > 0 && (
          <AuditTable events={state.events} loading={state.status === 'loading'} />
        )}
      </div>

      {state.events.length > 0 && (
        <p className="mt-3 text-xs text-t3">
          Showing {state.events.length} event{state.events.length === 1 ? '' : 's'}
          {reachedLimit && ' (limit reached — increase it to see more)'}.
        </p>
      )}
    </div>
  );
}

// ─── Filters ────────────────────────────────────────────────────────────────

function Filters({ filters, onChange }) {
  return (
    <div className="mb-4 bg-white rounded-xl border border-bd p-4 flex flex-wrap gap-3">
      <FilterField label="Event type" className="flex-1 min-w-[200px]">
        <select
          value={filters.event_type}
          onChange={(e) => onChange('event_type', e.target.value)}
          className="w-full px-3 py-1.5 rounded-md border border-bd-strong bg-white text-sm"
        >
          {EVENT_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </FilterField>

      <FilterField label="Email contains" className="flex-1 min-w-[200px]">
        <input
          type="text"
          placeholder="user@example.com"
          value={filters.email}
          onChange={(e) => onChange('email', e.target.value)}
          className="w-full px-3 py-1.5 rounded-md border border-bd-strong text-sm"
        />
      </FilterField>

      <FilterField label="Limit" className="w-28">
        <select
          value={filters.limit}
          onChange={(e) => onChange('limit', parseInt(e.target.value, 10))}
          className="w-full px-3 py-1.5 rounded-md border border-bd-strong bg-white text-sm"
        >
          {LIMIT_OPTIONS.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
      </FilterField>
    </div>
  );
}

function FilterField({ label, className = '', children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-xs font-medium text-t3 mb-1">{label}</span>
      {children}
    </label>
  );
}

// ─── Table ──────────────────────────────────────────────────────────────────

function AuditTable({ events, loading }) {
  return (
    <div className={`overflow-x-auto ${loading ? 'opacity-60' : ''}`}>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-t3 border-b border-bd bg-s-2">
            <th className="px-4 py-2.5 font-medium">When</th>
            <th className="px-4 py-2.5 font-medium">Event</th>
            <th className="px-4 py-2.5 font-medium">Email</th>
            <th className="px-4 py-2.5 font-medium">IP</th>
            <th className="px-4 py-2.5 font-medium">Details</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-bd">
          {events.map((ev) => (
            <AuditRow key={ev.id} event={ev} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditRow({ event }) {
  const metadataSummary =
    event.metadata && typeof event.metadata === 'object'
      ? JSON.stringify(event.metadata)
      : null;

  return (
    <tr className="hover:bg-s-2">
      <td
        className="px-4 py-2.5 text-xs text-t3 whitespace-nowrap"
        title={formatAbsolute(event.created_at)}
      >
        {formatRelative(event.created_at)}
      </td>
      <td className="px-4 py-2.5 whitespace-nowrap">
        <EventBadge type={event.event_type} />
      </td>
      <td className="px-4 py-2.5 text-xs font-mono text-t2">
        {event.email || <span className="text-t3">—</span>}
      </td>
      <td className="px-4 py-2.5 text-xs font-mono text-t3">
        {event.ip || <span className="text-t3">—</span>}
      </td>
      <td className="px-4 py-2.5 text-xs text-t2 max-w-md">
        {metadataSummary ? (
          <code className="block truncate font-mono text-t3" title={metadataSummary}>
            {metadataSummary}
          </code>
        ) : (
          <span className="text-t3">—</span>
        )}
      </td>
    </tr>
  );
}

function EventBadge({ type }) {
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${eventColor(
        type,
      )}`}
    >
      {type}
    </span>
  );
}

function eventColor(type) {
  switch (type) {
    case 'auth.login':
      return 'bg-s-green text-ok border-bd';
    case 'auth.logout':
      return 'bg-s-2 text-t2 border-bd';
    case 'auth.rejected':
      return 'bg-err-bg text-err border-err-bd';
    case 'auth.oauth_error':
      return 'bg-warn-bg text-warn border-warn-bd';
    case 'auth.invalid_session':
      return 'bg-warn-bg text-warn border-warn-bd';
    default:
      if (type.startsWith('auth.')) return 'bg-s-2 text-t2 border-bd';
      if (type.startsWith('site.')) return 'bg-s-blue text-blue border-bd-blue';
      if (type.startsWith('system.')) return 'bg-s-blue text-blue border-bd-blue';
      if (type.startsWith('billing.')) return 'bg-s-dgrey text-t2 border-bd-strong';
      return 'bg-s-2 text-t2 border-bd';
  }
}

// ─── Time formatting ────────────────────────────────────────────────────────

const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

// SQLite CURRENT_TIMESTAMP returns "YYYY-MM-DD HH:MM:SS" in UTC (no TZ suffix).
// Normalize to ISO with explicit 'Z' so Date() parses as UTC, not local time.
function parseSqliteTimestamp(value) {
  if (!value) return null;
  if (value.includes('T')) return new Date(value);
  return new Date(value.replace(' ', 'T') + 'Z');
}

function formatRelative(value) {
  const date = parseSqliteTimestamp(value);
  if (!date) return '—';
  const diffSec = Math.round((Date.now() - date.getTime()) / 1000);
  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return rtf.format(-diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return rtf.format(-diffDay, 'day');
  return date.toLocaleDateString();
}

function formatAbsolute(value) {
  const date = parseSqliteTimestamp(value);
  if (!date) return '';
  return date.toLocaleString();
}
